/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2019
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { createError } from '../errors.js';
import { isFunction } from './type-checks-basic.js';
/**
 * Creates an {@link OneEventSource} with an {@link OneEventSourceConsumer} object with public
 * methods for consumers of the event, and non-public methods for the code that is the source of
 * the events and is publishing the interface.
 * @static
 * @returns {OneEventSource} Returns an {@link OneEventSource} object
 */
export function createEventSource() {
    const listeners = new Set();
    function addListener(fn) {
        if (listeners.has(fn)) {
            throw createError('EVS-CR1');
        }
        listeners.add(fn);
        if (isFunction(API.onListenerChange)) {
            API.onListenerChange(listeners.size - 1, listeners.size, fn);
        }
        return () => removeListener(fn);
    }
    function removeListener(fn) {
        if (!listeners.has(fn)) {
            throw createError('EVS-CR2');
        }
        listeners.delete(fn);
        if (API.onListenerChange !== null) {
            API.onListenerChange(listeners.size + 1, listeners.size, fn);
        }
    }
    function dispatch(data) {
        listeners.forEach(fn => fn(data));
    }
    async function dispatchAsync(data) {
        return await Promise.all([...listeners.values()].map(fn => fn(data)));
    }
    function hasListeners() {
        return listeners.size > 0;
    }
    const API = {
        consumer: {
            addListener,
            removeListener
        },
        onListenerChange: null,
        dispatch,
        dispatchAsync,
        hasListeners
    };
    return API;
}
//# sourceMappingURL=one-event-source.js.map